import React from 'react';

/**
 * Каркас формы кабинета — экран «Редактирование» из макета «QPrice Кабинет».
 *
 * Один каркас на профиль компании, позицию меню и скидку: шапка с
 * надзаголовком и действиями, слева разделы (если их больше одного),
 * справа карточки полей, внизу — что произойдёт после сохранения.
 */

export interface FormTab {
  key: string;
  label: string;
  /** Счётчик справа: сколько в разделе уже заполнено. */
  meta?: string;
}

interface FormShellProps {
  /** Надзаголовок: где мы находимся. Например «ПРОФИЛЬ · QPRICE АСТАНА». */
  eyebrow: string;
  title: string;
  /** Статус черновика. Молчит, пока сохранять нечего. */
  isDirty?: boolean;
  dirtyLabel?: string;
  cleanLabel?: string;
  /** Кнопки шапки: «Отменить», «Сохранить». */
  actions?: React.ReactNode;
  tabs?: FormTab[];
  activeTab?: string;
  onTabChange?: (key: string) => void;
  /** Идентификатор `<form>`, к которому относятся вкладки. */
  tabsLabel?: string;
  footer?: React.ReactNode;
  children: React.ReactNode;
}

export function FormShell({
  eyebrow,
  title,
  isDirty,
  dirtyLabel = 'Есть несохранённые изменения',
  cleanLabel = 'Всё сохранено',
  actions,
  tabs,
  activeTab,
  onTabChange,
  tabsLabel = 'Разделы формы',
  footer,
  children,
}: FormShellProps) {
  const hasTabs = !!tabs && tabs.length > 1;

  return (
    <div className="qp-form">
      <header className="qp-form-head">
        <div style={{ minWidth: 0 }}>
          <span className="qp-form-eyebrow">{eyebrow}</span>
          <h1 className="qp-form-title">{title}</h1>
        </div>
        <div className="qp-form-head-actions">
          {isDirty !== undefined && (
            <span className={`qp-form-state${isDirty ? ' qp-form-state--dirty' : ''}`} role="status">
              {isDirty ? dirtyLabel : cleanLabel}
            </span>
          )}
          {actions}
        </div>
      </header>

      <div className={`qp-form-body${hasTabs ? ' qp-form-body--nav' : ''}`}>
        {hasTabs && (
          <nav className="qp-form-nav" role="tablist" aria-label={tabsLabel}>
            {tabs!.map((tab) => (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.key}
                className="qp-form-navitem"
                onClick={() => onTabChange?.(tab.key)}
              >
                <span className="qp-form-navitem-label">{tab.label}</span>
                {tab.meta && <span className="qp-form-navitem-meta">{tab.meta}</span>}
              </button>
            ))}
          </nav>
        )}

        <div className="qp-form-panes">
          {children}
          {footer}
        </div>
      </div>
    </div>
  );
}

interface FormCardProps {
  title: string;
  hint?: string;
  children: React.ReactNode;
}

export function FormCard({ title, hint, children }: FormCardProps) {
  return (
    <section className="qp-form-card">
      <div className="qp-form-card-head">
        <h2 className="qp-form-card-title">{title}</h2>
        {hint && <p className="qp-form-card-hint">{hint}</p>}
      </div>
      {children}
    </section>
  );
}

interface FormFootProps {
  /** Что произойдёт после сохранения — обещание, а не поздравление. */
  note: string;
  children?: React.ReactNode;
}

export function FormFoot({ note, children }: FormFootProps) {
  return (
    <div className="qp-form-foot">
      <span className="qp-form-foot-icon" aria-hidden>
        i
      </span>
      <span className="qp-form-foot-text">{note}</span>
      {children}
    </div>
  );
}
